import { useMutation, useQueryClient } from '@tanstack/react-query';
import { privateApi } from '../../../api/api';

// Tipe untuk variabel yang diterima oleh fungsi mutasi
interface AttachPropertyVariables {
    userId: string;
    propertyId: string;
}

// Tipe untuk respons sukses dari API (diasumsikan sederhana)
// Kita akan buat skema Zod sederhana di sini untuk validasi.
import { z } from 'zod';

const AttachPropertyResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number(),
    data: z.any().optional(), // Data bisa beragam atau tidak ada sama sekali
});

type AttachPropertyResponse = z.infer<typeof AttachPropertyResponseSchema>;

// Endpoint API untuk operasi attach
const API_ENDPOINT = '/v4/user-property/attach';

/**
 * Hook kustom untuk menghubungkan (attach) sebuah properti ke seorang pengguna (Versi 4).
 * Mengenkapsulasi logika mutasi, pemanggilan API, dan invalidasi cache.
 * 
 * @returns Hasil dari React Query `useMutation`.
 */
export const useAttachPropertyV4 = () => {
    const queryClient = useQueryClient();

    return useMutation<AttachPropertyResponse, Error, AttachPropertyVariables>({
        /**
         * Fungsi mutasi yang akan dijalankan saat `mutate()` dipanggil.
         * @param variables - Objek yang berisi `userId` dan `propertyId`.
         */
        mutationFn: async ({ userId, propertyId }: AttachPropertyVariables): Promise<AttachPropertyResponse> => {
            if (!userId || !propertyId) {
                throw new Error("User ID and Property ID are required to attach a property.");
            }

            try {
                // Melakukan request POST dengan body yang berisi ID.
                const response = await privateApi.post(API_ENDPOINT, {
                    user_id: userId,
                    property_id: propertyId
                });

                // Validasi struktur data respons dari API.
                const validationResult = AttachPropertyResponseSchema.safeParse(response.data);

                if (!validationResult.success) {
                    console.error("API response validation failed in useAttachPropertyV4:", validationResult.error.flatten());
                    throw new Error("Invalid data format received from the server after attaching property.");
                }

                return validationResult.data;
            } catch (error) {
                console.error('Failed to attach property (V4):', error);
                throw error;
            }
        },

        /**
         * Callback yang dijalankan setelah mutasi berhasil.
         * Ini adalah tempat untuk invalidasi cache agar UI sinkron.
         */
        onSuccess: (_data, variables) => {
            const { userId } = variables;

            // 1. Invalidate semua query yang berhubungan dengan daftar pengguna.
            // Ini akan membuat tabel pengguna di halaman utama diperbarui,
            // terutama jika ada kolom yang menampilkan jumlah atau nama properti.
            queryClient.invalidateQueries({ queryKey: ['users', 'v4', 'list'] });

            // 2. Invalidate query spesifik untuk detail pengguna yang baru saja diubah.
            // Ini penting jika dialog `AssignProperty` tetap terbuka atau
            // jika ada halaman detail pengguna yang sedang ditampilkan.
            queryClient.invalidateQueries({ queryKey: ['users', 'v4', 'detail', userId] });

            // 3. Invalidate query untuk daftar semua properti, jika ada.
            // Ini berguna agar daftar properti yang "tersedia" di dialog diperbarui.
            queryClient.invalidateQueries({ queryKey: ['properties', 'v4', 'all'] });
        },

        /**
         * Callback yang dijalankan jika mutasi gagal.
         */
        onError: () => {
            // No-op. Penanganan error utama ada di `mutationFn` dan di komponen yang memanggil.
        },
    });
};